import { useMemo } from 'react'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'

import { assetUrl } from 'utils/url'

import type { KinerjaPraktikPembelajaranDimensi } from 'types/kinerja'

interface DialogDimensiDetailProps extends Omit<DialogProps, 'children'> {
  contentPraktikPembelajaran: Array<
    Pick<
      KinerjaPraktikPembelajaranDimensi,
      'dimensiId' | 'dimensiNama' | 'skor'
    >
  >
  satuanPendidikan: string
  note?: string
}

const getDimensiStyle = (skor: number) => {
  switch (skor) {
    case 1:
      return {
        value: 'Kurang',
        color: 'bg-red-50',
      }
    case 2:
      return {
        value: 'Cukup',
        color: 'bg-orange-40',
      }
    default:
      return {
        value: 'Baik',
        color: 'bg-green-50',
      }
  }
}

const DEFAULT_SUPERIOR_NOTE = `Anda dapat berdiskusi dengan atasan untuk mempelajari Rapor Pendidikan Satdik Anda atau pun info lainnya tentang tahap Perencanaan.`

export default function DialogDimensiDetail({
  contentPraktikPembelajaran,
  satuanPendidikan,
  note = DEFAULT_SUPERIOR_NOTE,
  ...props
}: DialogDimensiDetailProps) {
  const dimensiList = useMemo(() => {
    return [
      {
        label: 'D.1.1 Manajemen Kelas',
        description: `Pengelolaan kelas yang mendukung pembelajaran serta penerapan penghargaan dan sanksi secara proporsional.`,
        ...getDimensiStyle(
          contentPraktikPembelajaran.find(
            (dimensi) => dimensi.dimensiNama === 'Manajemen Kelas'
          )?.skor
        ),
      },
      {
        label: 'D.1.2 Dukungan Psikologis',
        description: `Praktik pembelajaran yang memenuhi kebutuhan psikologis siswa untuk menumbuhkan kepercayaan diri dan perasaan diterima tanpa dibeda-bedakan.`,
        ...getDimensiStyle(
          contentPraktikPembelajaran.find(
            (dimensi) => dimensi.dimensiNama === 'Dukungan Psikologis'
          )?.skor
        ),
      },
      {
        label: 'D.1.3 Metode Pembelajaran',
        description: `Praktik pembelajaran interaktif yang sesuai dengan tujuan pembelajaran dan karakteristik siswa.`,
        ...getDimensiStyle(
          contentPraktikPembelajaran.find(
            (dimensi) => dimensi.dimensiNama === 'Metode Pembelajaran'
          )?.skor
        ),
      },
    ]
  }, [contentPraktikPembelajaran])

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent aria-labelledby="dialog-dimensi-detail-title">
        <DialogHeader className="flex gap-4 items-center justify-between">
          <div className="space-y-2">
            <Text
              variant="subheading"
              color="subdued"
              className="uppercase"
              as="h3"
              id="dialog-dimensi-detail-title"
            >
              Rapor Pendidikan Satuan Pendidikan Anda
            </Text>
            <Text variant="heading-md">{satuanPendidikan}</Text>
          </div>
          <Button
            size="md"
            color="white"
            as="a"
            href="https://raporpendidikan.kemdikbud.go.id/download-report"
            target="_blank"
            rel="noreferrer"
            type={undefined}
            className="flex items-center"
          >
            Ke sistem Rapor Pendidikan
            <Icon>keyboard_arrow_right</Icon>
          </Button>
        </DialogHeader>

        <div className="p-6 space-y-4">
          <div className="space-y-8 max-w-3xl">
            <section className="flex items-center gap-5">
              <img
                className="w-16 h-16 flex-shrink-0 flex-grow-0"
                alt=""
                src={assetUrl('/illustrations/indicator-illustration.svg')}
                width={64}
                height={64}
              />
              <Text variant="action">Kualitas Pembelajaran</Text>
              <div className="border-r border-gray-20 h-16">&nbsp;</div>
              <Text color="subdued">
                Kualitas pengelolaan kelas dan penyelenggaraan pembelajaran
                interaktif yang sesuai dengan tujuan pembelajaran dan
                karakteristik siswa.
              </Text>
            </section>

            <section className="space-y-4">
              <Text variant="heading-sm">Dimensi</Text>

              <ul className="border border-subdued rounded">
                {dimensiList.map((dimensi, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-4 p-3 border-b last:border-b-0 border-subdued"
                  >
                    <div className="space-y-1 flex-1">
                      <Text variant="action">{dimensi.label}</Text>
                      <Text variant="body-sm" color="subdued">
                        {dimensi.description}
                      </Text>
                    </div>

                    <div
                      className={clsx('px-6 py-2 rounded-full', dimensi.color)}
                    >
                      <Text color="inverse" variant="body-bold">
                        {dimensi.value}
                      </Text>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="p-4 rounded bg-surface-informational-subdued">
                <Text>
                  <strong>Catatan</strong>: {note}
                </Text>
              </div>
            </section>
          </div>

          <Button
            size="lg"
            onClick={() => props.onClose?.()}
            className="mx-auto"
          >
            Saya mengerti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
